import React, { useEffect, useState } from "react";
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import MaterialButton from '@mui/material/Button';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DatePicker from "react-datepicker";
import { nb } from 'date-fns/locale';
import "react-datepicker/dist/react-datepicker.css";
import "./date-picker.css";

import GroupSelect from "../GroupSelect";
import SeasonSwitch from "../SeasonSwitch";
import winterBackground from '../../assets/Bakgrunn_aktive-deg_winter.png';
import summerBackground from '../../assets/Bakgrunn_aktive-deg_summer.png';

const norwegianDatePickerLocale = {
    ...nb,
    localize: {
        ...nb.localize,
        month: (month, options) => {
            const monthName = nb.localize.month(month, options);
            return monthName.charAt(0).toUpperCase() + monthName.slice(1);
        },
    },
};

export default function NewUserDialog({
    open,
    title,
    isEditing,
    isDeleting,
    onClose,
    onSave,
    onDelete,
    userInst,
    pNR,
    onPNRChange,
    name,
    onNameChange,
    group,
    onGroupChange,
    arrivalDate,
    onArrivalDateChange,
    departureDate,
    onDepartureDateChange,
    isWinter,
    onIsWinterChange,
}) {
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    useEffect(() => {
        if (!open) {
            setConfirmingDelete(false);
        }
    }, [open]);

    const isGroupMissing = userInst === '01' && !group.trim();
    const isSaveDisabled = !String(pNR).trim() || !name.trim() || isGroupMissing;
    const seasonAccent = isWinter ? '#439AC2' : '#7FAF84';
    const seasonAccentHover = isWinter ? '#2F86AD' : '#6E9672';
    const seasonOutline = isWinter ? '#9FC8DA' : '#B6D0B8';
    const seasonBackground = isWinter ? winterBackground : summerBackground;
    const handleClose = () => {
        if (isDeleting) {
            return;
        }
        setConfirmingDelete(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    position: 'relative',
                    overflow: 'hidden',
                    maxWidth: confirmingDelete ? 460 : 770,
                    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.2)',
                    backgroundColor: '#FFFFFF',
                    backgroundImage: `url(${seasonBackground})`,
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(253, 254, 255, 0.25)',
                        pointerEvents: 'none',
                    },
                    '& > *': {
                        position: 'relative',
                        zIndex: 1,
                    },
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.68)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        borderRadius: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: seasonOutline,
                        borderWidth: '3px',
                        borderRadius: '14px',
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: seasonOutline,
                        borderWidth: '3px',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: seasonOutline,
                        borderWidth: '3px',
                    },
                },
            }}
        >
            <Box
                sx={{
                    mx: confirmingDelete ? 3 : 6,
                    my: 5,
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.72)',
                    borderRadius: '14px',
                    backgroundColor: 'rgba(248, 252, 254, 0.9)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                }}
            >
                <DialogTitle sx={{ px: 5, pt: 4, pb: 1, color: '#17233D', fontSize: 28, fontWeight: 700 }}>
                    {confirmingDelete ? 'Bekreft sletting' : title}
                </DialogTitle>
                <DialogContent sx={{ px: 5, py: 1.5 }}>
                    {confirmingDelete ? (
                        <DialogContentText sx={{ color: '#687895', fontSize: 17, lineHeight: 1.6 }}>
                            Vil du slette {name} (ID: {pNR})? Denne handlingen kan ikke angres.
                        </DialogContentText>
                    ) : (
                        <>
                            <DialogContentText sx={{ mb: 2.5, color: '#687895', fontSize: 18 }}>
                                Legg inn nødvendige pasientopplysninger. Alle felt er obligatoriske.
                            </DialogContentText>
                            <Box sx={{ mb: 2.5 }}>
                                <Typography sx={{ mb: 0.5, color: '#17233D', fontSize: 15, fontWeight: 500 }}>
                                    Pasientnummer <Box component="span" sx={{ color: seasonAccent }}>*</Box>
                                </Typography>
                                <TextField
                                    autoFocus
                                    id="patientNumber"
                                    fullWidth
                                    variant="outlined"
                                    sx={{ '& .MuiOutlinedInput-root': { height: 48, minHeight: 48 }, '& .MuiInputBase-input': { padding: '8px 14px' } }}
                                    value={pNR}
                                    onChange={onPNRChange}
                                    required
                                />
                            </Box>
                            <Box sx={{ mb: 2.5 }}>
                                <Typography sx={{ mb: 0.5, color: '#17233D', fontSize: 15, fontWeight: 500 }}>
                                    Fornavn <Box component="span" sx={{ color: seasonAccent }}>*</Box>
                                </Typography>
                                <TextField
                                    autoFocus
                                    id="patientName"
                                    fullWidth
                                    variant="outlined"
                                    sx={{ '& .MuiOutlinedInput-root': { height: 48, minHeight: 48 }, '& .MuiInputBase-input': { padding: '8px 14px' } }}
                                    value={name}
                                    onChange={onNameChange}
                                    required
                                />
                            </Box>
                            <GroupSelect userInst={userInst} group={group} onChange={onGroupChange} required isWinter={isWinter} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, mt: 0.5 }}>
                                <Box>
                                    <Typography sx={{ mb: 0.5, color: '#17233D', fontSize: 15, fontWeight: 500 }}>
                                        Ankomstdato <Box component="span" sx={{ color: seasonAccent }}>*</Box>
                                    </Typography>
                                    <DatePicker
                                        selected={arrivalDate}
                                        onChange={onArrivalDateChange}
                                        dateFormat="dd.MM.yyyy"
                                        locale={norwegianDatePickerLocale}
                                        customInput={(
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                sx={{ '& .MuiOutlinedInput-root': { height: 48, minHeight: 48 } }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end"><CalendarTodayOutlinedIcon sx={{ color: '#425575', fontSize: 20 }} /></InputAdornment>,
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                                <Box>
                                    <Typography sx={{ mb: 0.5, color: '#17233D', fontSize: 15, fontWeight: 500 }}>
                                        Avreisedato <Box component="span" sx={{ color: seasonAccent }}>*</Box>
                                    </Typography>
                                    <DatePicker
                                        selected={departureDate}
                                        minDate={arrivalDate}
                                        onChange={onDepartureDateChange}
                                        dateFormat="dd.MM.yyyy"
                                        locale={norwegianDatePickerLocale}
                                        customInput={(
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                sx={{ '& .MuiOutlinedInput-root': { height: 48, minHeight: 48 } }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end"><CalendarTodayOutlinedIcon sx={{ color: '#425575', fontSize: 20 }} /></InputAdornment>,
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                            <SeasonSwitch userInst={userInst} isWinter={isWinter} onChange={onIsWinterChange} accent={seasonAccent} accentHover={seasonAccentHover} outline={seasonOutline} />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 5, py: 2.5, borderTop: '1px solid #E5EAF1', justifyContent: 'flex-end', gap: 1.5 }}>
                    {confirmingDelete ? (
                        <>
                            <MaterialButton onClick={() => setConfirmingDelete(false)} disabled={isDeleting} sx={{ color: seasonAccent, fontSize: 15, fontWeight: 700, textTransform: 'none' }}>
                                Avbryt
                            </MaterialButton>
                            <MaterialButton
                                onClick={onDelete}
                                disabled={isDeleting}
                                variant="contained"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                sx={{ px: 2.5, height: 46, borderRadius: '8px', fontWeight: 700, textTransform: 'none' }}
                            >
                                {isDeleting ? 'Sletter...' : 'Ja, slett bruker'}
                            </MaterialButton>
                        </>
                    ) : (
                        <>
                            {isEditing && (
                                <MaterialButton
                                    onClick={() => setConfirmingDelete(true)}
                                    disabled={isDeleting}
                                    color="error"
                                    startIcon={<DeleteOutlineIcon />}
                                    sx={{ mr: 'auto', fontSize: 15, fontWeight: 700, textTransform: 'none' }}
                                >
                                    Slett bruker
                                </MaterialButton>
                            )}
                            <MaterialButton onClick={handleClose} sx={{ color: seasonAccent, fontSize: 16, fontWeight: 700, textTransform: 'none' }}>Avbryt</MaterialButton>
                            <MaterialButton
                                onClick={onSave}
                                disabled={isSaveDisabled}
                                variant="contained"
                                sx={{ px: 4, height: 50, borderRadius: '8px', bgcolor: seasonAccent, fontSize: 16, fontWeight: 700, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: seasonAccentHover, boxShadow: 'none' } }}
                            >
                                Lagre
                            </MaterialButton>
                        </>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    );
}
